/* ==========================================================================
   vv-product.js — Vivendo native product page logic
   - Variant pill picker (availability dimming, URL sync, media jump)
   - Price / PRP / save-chip / SKU / Klarna / ATC state updates
   - Gallery slider (track transform, arrows, thumbs, swipe)
   - Qty steppers (main + sticky)
   - AJAX add-to-cart (/cart/add.js) → header count update; UpCart drawer
     opens automatically via its fetch hook. Falls back to native submit.
   - Satisfaction number (localStorage.customerSatisfaction, 90-98)
   - Extra shipping fee notices (voluminos / agabaritic)
   - Description "Vezi mai mult / Vezi mai puțin"
   - Sticky ATC bar (IntersectionObserver on the main ATC button)
   ========================================================================== */
(() => {
  'use strict';

  const init = () => {
    const root = document.querySelector('[data-vv-main-product]');
    const productScript = document.querySelector('script[data-vv-product]');
    if (!root || !productScript) return;

    let product;
    try {
      product = JSON.parse(productScript.textContent);
    } catch (e) {
      return;
    }
    const variants = product.variants || [];
    const optionCount = (product.options || []).length;

    /* ── Money formatting ─────────────────────────────────────────────── */
    const moneyFormat = root.dataset.moneyFormat || '{{amount}} lei';
    const formatMoney = (cents) => {
      const val = (cents / 100);
      const withDelims = (num, decimals, thousands, decimalSep) => {
        const fixed = num.toFixed(decimals);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.length > 1 ? parts[0] + decimalSep + parts[1] : parts[0];
      };
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => {
        switch (key) {
          case 'amount': return withDelims(val, 2, ',', '.');
          case 'amount_no_decimals': return withDelims(val, 0, ',', '.');
          case 'amount_with_comma_separator': return withDelims(val, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return withDelims(val, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return withDelims(val, 2, "'", '.');
          default: return m;
        }
      });
    };

    /* ── Element refs ─────────────────────────────────────────────────── */
    const priceEl = root.querySelector('[data-vv-price]');
    const prpRow = root.querySelector('[data-vv-prp-row]');
    const compareEl = root.querySelector('[data-vv-compare]');
    const saveChip = root.querySelector('[data-vv-save]');
    const savePctEl = root.querySelector('[data-vv-save-pct]');
    const skuEl = root.querySelector('[data-vv-sku]');
    const klarnaEl = root.querySelector('[data-vv-klarna]');
    const labelAdd = root.dataset.labelAdd || 'Adauga in cos';
    const labelOos = root.dataset.labelOos || 'Stoc epuizat';
    const stickyBar = document.querySelector('[data-vv-sticky]');
    const stickySelect = document.querySelector('[data-vv-sticky-select]');
    const stickyPrice = document.querySelector('[data-vv-sticky-price]');

    // The main ATC button sits outside its <form> (linked via form="" attribute),
    // so resolve buttons through form ownership instead of DOM nesting.
    const allAtcButtons = Array.from(document.querySelectorAll('[data-vv-atc]'));
    const atcButtonFor = (form) => allAtcButtons.find((b) => b.form === form) || form.querySelector('[data-vv-atc]');

    /* ── Current variant resolution ───────────────────────────────────── */
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    let currentVariant =
      (urlVariantId && variants.find((v) => String(v.id) === String(urlVariantId))) ||
      variants.find((v) => v.available) ||
      variants[0];

    const selectedOptions = currentVariant ? currentVariant.options.slice() : [];

    const findVariant = (opts) =>
      variants.find((v) => v.options.every((o, i) => o === opts[i]));

    /* ── Gallery ──────────────────────────────────────────────────────── */
    const track = root.querySelector('[data-vv-track]');
    const slides = Array.from(root.querySelectorAll('[data-vv-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-vv-thumb]'));
    let galleryIndex = 0;

    const goTo = (i) => {
      if (!track || !slides.length) return;
      galleryIndex = Math.max(0, Math.min(i, slides.length - 1));
      track.style.transform = 'translateX(-' + (galleryIndex * 100) + '%)';
      thumbs.forEach((t, ti) => t.classList.toggle('is-active', ti === galleryIndex));
      const activeThumb = thumbs[galleryIndex];
      if (activeThumb && activeThumb.scrollIntoView) {
        activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };

    const prevBtn = root.querySelector('[data-vv-prev]');
    const nextBtn = root.querySelector('[data-vv-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => goTo(galleryIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(galleryIndex + 1));
    thumbs.forEach((t) =>
      t.addEventListener('click', () => goTo(parseInt(t.dataset.index, 10) || 0))
    );

    if (track) {
      let startX = null;
      let startY = null;
      track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      track.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          goTo(galleryIndex + (dx < 0 ? 1 : -1));
        }
        startX = null;
        startY = null;
      }, { passive: true });
    }

    const jumpToVariantMedia = (variant) => {
      if (!variant || !variant.featured_media) return;
      const idx = slides.findIndex(
        (s) => String(s.dataset.mediaId) === String(variant.featured_media.id)
      );
      if (idx >= 0) goTo(idx);
    };

    /* ── Pill availability ────────────────────────────────────────────── */
    const pills = Array.from(root.querySelectorAll('[data-vv-option-value]'));

    const refreshPills = () => {
      pills.forEach((pill) => {
        const idx = parseInt(pill.dataset.optionIndex, 10);
        const value = pill.dataset.value;
        const isSelected = selectedOptions[idx] === value;
        pill.classList.toggle('is-selected', isSelected);
        pill.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

        const candidate = selectedOptions.slice();
        candidate[idx] = value;
        const match = findVariant(candidate);
        pill.classList.toggle('is-unavailable', !(match && match.available));
      });
    };

    /* ── Variant application ──────────────────────────────────────────── */
    const setVariant = (variant, opts) => {
      if (!variant) return;
      const options = opts || {};
      currentVariant = variant;
      for (let i = 0; i < optionCount; i++) selectedOptions[i] = variant.options[i];

      // Price
      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      // PRP + save chip
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (prpRow) {
        prpRow.hidden = !hasCompare;
        if (compareEl && hasCompare) compareEl.textContent = formatMoney(variant.compare_at_price);
      }
      if (saveChip) {
        const pct = hasCompare
          ? Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
          : 0;
        saveChip.hidden = pct === 0;
        if (savePctEl) savePctEl.textContent = String(pct);
      }

      // SKU
      if (skuEl) {
        skuEl.textContent = variant.sku || '';
        const skuWrap = skuEl.closest('p');
        if (skuWrap) skuWrap.hidden = !variant.sku;
      }

      // ATC buttons + hidden inputs (main + sticky).
      // Buttons may sit outside their form element (form="" attribute),
      // so resolve them through the button's form ownership, not the DOM tree.
      document.querySelectorAll('[data-vv-atc-form]').forEach((form) => {
        const input = form.querySelector('[data-vv-id-input]');
        if (input) input.value = variant.id;
        const btn = atcButtonFor(form);
        if (btn) {
          btn.disabled = !variant.available;
          const label = btn.querySelector('[data-vv-atc-label]');
          if (label) label.textContent = variant.available ? labelAdd : labelOos;
        }
      });

      // Klarna
      if (klarnaEl) {
        klarnaEl.setAttribute('data-purchase-amount', String(variant.price));
        try {
          if (window.Klarna && window.Klarna.OnsiteMessaging && window.Klarna.OnsiteMessaging.refresh) {
            window.Klarna.OnsiteMessaging.refresh();
          }
        } catch (e) { /* noop */ }
      }

      // URL
      if (!options.skipUrl && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      // Sticky bar sync
      if (stickySelect) stickySelect.value = String(variant.id);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      // Gallery
      jumpToVariantMedia(variant);

      refreshPills();
      updateExtraFee(variant);

      document.dispatchEvent(new CustomEvent('vv:variant-change', {
        detail: { variant: variant, product: product }
      }));
    };

    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const idx = parseInt(pill.dataset.optionIndex, 10);
        const value = pill.dataset.value;
        const candidate = selectedOptions.slice();
        candidate[idx] = value;
        let variant = findVariant(candidate);
        if (!variant) {
          // No exact combo — fall back to first variant carrying this value.
          variant = variants.find((v) => v.options[idx] === value);
        }
        if (variant) setVariant(variant);
      });
    });

    if (stickySelect) {
      stickySelect.addEventListener('change', () => {
        const variant = variants.find((v) => String(v.id) === stickySelect.value);
        if (variant) setVariant(variant);
      });
    }

    /* ── Qty steppers ─────────────────────────────────────────────────── */
    document.querySelectorAll('[data-vv-qty]').forEach((box) => {
      const input = box.querySelector('[data-vv-qty-input]');
      const minus = box.querySelector('[data-vv-qty-minus]');
      const plus = box.querySelector('[data-vv-qty-plus]');
      const clamp = () => {
        const v = parseInt(input.value, 10);
        input.value = isNaN(v) || v < 1 ? 1 : v;
      };
      if (minus) minus.addEventListener('click', () => { clamp(); input.value = Math.max(1, parseInt(input.value, 10) - 1); });
      if (plus) plus.addEventListener('click', () => { clamp(); input.value = parseInt(input.value, 10) + 1; });
      if (input) input.addEventListener('change', clamp);
    });

    /* ── AJAX add to cart ─────────────────────────────────────────────── */
    const updateCartCount = () => {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          document.querySelectorAll('[data-cart-count], .t4s-count-box').forEach((el) => {
            el.textContent = cart.item_count;
            el.classList.remove('t4s-op-0');
          });
        })
        .catch(() => { /* noop */ });
    };

    // Resigilat (open-box) offer: expandable panel + variant select syncing
    // the panel's quick-add button and price.
    const refurbRoot = document.querySelector('[data-vv-refurb]');
    if (refurbRoot) {
      const rToggle = refurbRoot.querySelector('[data-vv-refurb-toggle]');
      const rPanel = refurbRoot.querySelector('.vv-refurb__panel');
      if (rToggle && rPanel) {
        rToggle.addEventListener('click', () => {
          const open = rToggle.getAttribute('aria-expanded') === 'true';
          rToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
          rPanel.hidden = open;
          refurbRoot.classList.toggle('is-open', !open);
        });
      }
      const rSel = refurbRoot.querySelector('[data-vv-refurb-select]');
      const rPrice = refurbRoot.querySelector('[data-vv-refurb-price]');
      const rBtn = refurbRoot.querySelector('.vv-refurb__panel [data-vv-quick-add]');
      const rHeadPrice = refurbRoot.querySelector('[data-vv-refurb-headprice]');
      const rCompare = refurbRoot.querySelector('[data-vv-refurb-compare]');
      const rSave = refurbRoot.querySelector('[data-vv-refurb-save]');
      const rThumb = refurbRoot.querySelector('.vv-refurb__thumb img');
      const selectedCents = () => {
        const opt = rSel && rSel.options[rSel.selectedIndex];
        return opt ? parseInt(opt.dataset.cents, 10) : NaN;
      };
      const updateSaveBadge = () => {
        if (!rSave) return;
        const main = parseInt(refurbRoot.dataset.mainPrice, 10);
        const cents = rSel ? selectedCents() : NaN;
        const refurbCents = Number.isNaN(cents) ? main : cents;
        const pct = main > refurbCents ? Math.round(((main - refurbCents) / main) * 100) : 0;
        rSave.hidden = pct <= 0;
        rSave.textContent = '-' + pct + '%';
      };
      if (rSel) {
        rSel.addEventListener('change', () => {
          const opt = rSel.options[rSel.selectedIndex];
          if (!opt) return;
          if (rBtn) rBtn.dataset.variantId = rSel.value;
          if (rPrice && opt.dataset.price) rPrice.textContent = opt.dataset.price;
          if (rHeadPrice && opt.dataset.price) rHeadPrice.textContent = opt.dataset.price;
          if (rThumb && opt.dataset.img) {
            rThumb.src = opt.dataset.img;
            rThumb.removeAttribute('srcset');
          }
          updateSaveBadge();
        });
      }
      // Keep the struck-through compare price + discount badge in sync with
      // the MAIN product's selected variant.
      document.addEventListener('vv:variant-change', (e) => {
        const variant = e.detail && e.detail.variant;
        if (!variant) return;
        refurbRoot.dataset.mainPrice = variant.price;
        if (rCompare) rCompare.textContent = formatMoney(variant.price);
        updateSaveBadge();
      });
    }

    // Judge.me stars under the title: click scrolls to the reviews widget
    // (opening its accordion first)
    const jdgmBadge = document.querySelector('.vv-mp__reviews-row') ||
      document.querySelector('.vv-mp__reviews.jdgm-preview-badge');
    if (jdgmBadge) {
      jdgmBadge.style.cursor = 'pointer';
      jdgmBadge.addEventListener('click', () => {
        const widget = document.getElementById('judgeme_product_reviews') ||
          document.querySelector('.jdgm-review-widget');
        if (!widget) return;
        const det = widget.closest('details');
        if (det) det.setAttribute('open', '');
        widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    // Judge.me lazy-loads its review-form script only when the write-review
    // link scrolls into view — inside our closed accordion that never fires,
    // so the FIRST click on "Scrie o recenzie" would only trigger the script
    // load and look dead. When the reviews accordion opens, fire a synthetic
    // scroll so Judge.me's own viewport preloader kicks in immediately.
    const jdgmWidget = document.getElementById('judgeme_product_reviews') ||
      document.querySelector('.jdgm-review-widget');
    const jdgmDetails = jdgmWidget && jdgmWidget.closest('details');
    if (jdgmDetails) {
      const preloadJdgmForm = () => {
        if (!jdgmDetails.open) return;
        let fired = 0;
        const tick = setInterval(() => {
          window.dispatchEvent(new Event('scroll'));
          if (++fired >= 5) clearInterval(tick);
        }, 400);
      };
      jdgmDetails.addEventListener('toggle', preloadJdgmForm);
      preloadJdgmForm();
    }

    // Judge.me binds the real write-review handler only after its form
    // script lazy-loads + an async modal setup finishes; a click landing
    // before that does nothing. If the click arrived before the handler
    // existed, wait for it to be bound and replay the click exactly once.
    // A click that Judge.me already handled is never replayed (so closing
    // the modal sticks).
    if (jdgmWidget) {
      const jdgmLinkIsLive = (link) => {
        try {
          const $ = window.jdgm && window.jdgm.$;
          const evs = $ && $._data(link, 'events');
          return !!(evs && evs.click && evs.click.length);
        } catch (err) { return true; }
      };
      document.addEventListener('click', (e) => {
        const link = e.target.closest && e.target.closest('.jdgm-write-rev-link');
        if (!link || link.dataset.vvRetry || jdgmLinkIsLive(link)) return;
        let attempts = 0;
        const replay = () => {
          if (jdgmLinkIsLive(link)) {
            link.dataset.vvRetry = '1';
            link.click();
            delete link.dataset.vvRetry;
            return;
          }
          if (++attempts < 8) setTimeout(replay, 500);
        };
        setTimeout(replay, 500);
      }, true);
    }

    // Back-in-stock notify: shown only while the selected variant is OOS;
    // posts the subscription to the TenQ backend.
    const bisRoot = document.querySelector('[data-vv-bis]');
    if (bisRoot) {
      document.addEventListener('vv:variant-change', (e) => {
        const variant = e.detail && e.detail.variant;
        if (!variant) return;
        bisRoot.hidden = !!variant.available;
        bisRoot.dataset.variantId = variant.id;
        bisRoot.dataset.variantTitle = variant.title || '';
      });
      const bisForm = bisRoot.querySelector('[data-vv-bis-form]');
      const bisMsg = bisRoot.querySelector('[data-vv-bis-msg]');
      const showBisMsg = (text, ok) => {
        if (!bisMsg) return;
        bisMsg.textContent = text;
        bisMsg.classList.toggle('is-ok', !!ok);
        bisMsg.classList.toggle('is-err', !ok);
        bisMsg.hidden = false;
      };
      if (bisForm) {
        bisForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const emailInput = bisForm.querySelector('input[name="email"]');
          const email = (emailInput && emailInput.value || '').trim();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
            showBisMsg('Te rugăm să introduci un email valid.', false);
            return;
          }
          const btn = bisForm.querySelector('.vv-bis__btn');
          if (btn) btn.disabled = true;
          fetch(bisRoot.dataset.api, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              shop: bisRoot.dataset.shop,
              productId: bisRoot.dataset.productId,
              variantId: bisRoot.dataset.variantId,
              email,
              productTitle: bisRoot.dataset.productTitle,
              variantTitle: bisRoot.dataset.variantTitle,
              productUrl: bisRoot.dataset.productUrl,
              website: (bisForm.querySelector('input[name="website"]') || {}).value || ''
            })
          })
            .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
            .then(({ ok, data }) => {
              if (ok && data.success) {
                bisForm.hidden = true;
                showBisMsg('Te-am notat! Îți trimitem un email imediat ce produsul revine în stoc.', true);
              } else {
                showBisMsg('Nu am putut salva cererea. Te rugăm să încerci din nou.', false);
                if (btn) btn.disabled = false;
              }
            })
            .catch(() => {
              showBisMsg('Nu am putut salva cererea. Te rugăm să încerci din nou.', false);
              if (btn) btn.disabled = false;
            });
        });
      }
    }

    // Extra-warranty upsell switch (adds the warranty product alongside the
    // main item at add-to-cart when enabled).
    const warrantySwitch = document.querySelector('[data-vv-warranty]');
    if (warrantySwitch) {
      warrantySwitch.addEventListener('click', () => {
        const on = warrantySwitch.getAttribute('aria-checked') === 'true';
        warrantySwitch.setAttribute('aria-checked', on ? 'false' : 'true');
      });
    }

    document.querySelectorAll('[data-vv-atc-form]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = atcButtonFor(form);
        if (btn && btn.disabled) return;
        const fd = new FormData(form);
        if (btn) btn.classList.add('is-loading');
        // When the warranty switch is on, send one JSON request with both
        // the main item and the warranty product.
        let body = fd;
        let headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
        const warrantyOn = warrantySwitch && warrantySwitch.getAttribute('aria-checked') === 'true';
        if (warrantyOn) {
          const mainId = parseInt(fd.get('id'), 10);
          const qty = parseInt(fd.get('quantity'), 10) || 1;
          const wId = parseInt(warrantySwitch.dataset.variantId, 10);
          if (mainId && wId) {
            body = JSON.stringify({ items: [{ id: mainId, quantity: qty }, { id: wId, quantity: 1 }] });
            headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
          }
        }
        fetch('/cart/add.js', {
          method: 'POST',
          body: body,
          headers: headers
        })
          .then((r) => {
            if (!r.ok) throw new Error('add-failed');
            return r.json();
          })
          .then(() => {
            updateCartCount();
            // Deterministic open signal for the theme's cart drawer (vv-cart.js).
            document.dispatchEvent(new CustomEvent('vv:cart:added'));
          })
          .catch(() => {
            // Fallback: native submit (full page reload to /cart/add).
            form.removeAttribute('data-vv-atc-form');
            form.submit();
          })
          .finally(() => {
            if (btn) btn.classList.remove('is-loading');
          });
      });
    });

    /* ── Satisfaction number ──────────────────────────────────────────── */
    (() => {
      const el = root.querySelector('[data-vv-satisfaction]');
      if (!el) return;
      let n;
      try {
        n = localStorage.getItem('customerSatisfaction');
        if (!n) {
          n = String(Math.floor(Math.random() * 9) + 90); // 90–98
          localStorage.setItem('customerSatisfaction', n);
        }
      } catch (e) {
        n = String(Math.floor(Math.random() * 9) + 90);
      }
      el.textContent = n;
    })();

    /* ── Extra shipping fee notices (exact GP wording) ────────────────── */
    const extraFeeEl = root.querySelector('[data-vv-extrafee]');
    const updateExtraFee = (variant) => {
      if (!extraFeeEl || !variant) return;
      const tags = product.tags || [];
      const weightKg = (variant.weight || 0) / 1000;
      let message = '';

      if (tags.includes('voluminos') && weightKg > 10) {
        // Round UP to the next started kg over 10 — couriers bill by started kg,
        // so any excess (e.g. 10.17 kg → 0.17) must charge at least 1 leu, not 0.
        const extraFee = Math.ceil(weightKg - 10);
        message += '<p>⚠️ Fiind un produs voluminos, se percepe o taxă suplimentară de transport de ' + extraFee + ' lei.</p>';
      }
      if (tags.includes('agabaritic')) {
        message += '<p>⚠️ Datorită greutății și volumetriei, la acest produs se percepe o taxă de livrare extra de 10 lei per bucată suplimentară.</p>';
      }
      extraFeeEl.innerHTML = message;
    };

    /* ── Description show more / less ─────────────────────────────────── */
    (() => {
      const wrap = document.querySelector('[data-vv-desc]');
      if (!wrap) return;
      const content = wrap.querySelector('[data-vv-desc-content]');
      const toggle = wrap.querySelector('[data-vv-desc-toggle]');
      if (!content || !toggle) return;
      const LIMIT = 400;
      const check = () => {
        if (content.scrollHeight > LIMIT + 40) {
          content.classList.add('is-collapsed');
          toggle.hidden = false;
        }
      };
      toggle.addEventListener('click', () => {
        const collapsed = content.classList.toggle('is-collapsed');
        toggle.textContent = collapsed
          ? (toggle.dataset.more || 'Vezi mai mult')
          : (toggle.dataset.less || 'Vezi mai puțin');
      });
      if (document.readyState === 'complete') check();
      else window.addEventListener('load', check, { once: true });
    })();

    /* ── Thumbnail slider (arrows + overflow detection) ───────────────── */
    (() => {
      const wrap = document.querySelector('[data-vv-thumbs-wrap]');
      if (!wrap) return;
      const track = wrap.querySelector('[data-vv-thumbs]');
      const prev = wrap.querySelector('[data-vv-thumbs-prev]');
      const next = wrap.querySelector('[data-vv-thumbs-next]');
      if (!track || !prev || !next) return;

      const update = () => {
        const overflow = track.scrollWidth - track.clientWidth > 2;
        prev.hidden = !overflow || track.scrollLeft <= 2;
        next.hidden = !overflow || track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      const page = (dir) => {
        track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
      };
      prev.addEventListener('click', () => page(-1));
      next.addEventListener('click', () => page(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      document.addEventListener('vv:variant-change', () => {
        const active = track.querySelector('.is-active');
        if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        update();
      });
      // Images load lazily — re-measure once they arrive.
      if ('requestIdleCallback' in window) requestIdleCallback(update); else setTimeout(update, 300);
      update();
    })();

    /* ── Sticky ATC bar visibility ────────────────────────────────────── */
    (() => {
      const mainAtc = document.getElementById('vv-atc-button');
      if (!stickyBar || !mainAtc || !('IntersectionObserver' in window)) return;
      stickyBar.hidden = false;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-visible', passed);
        });
      }, { threshold: 0 });
      io.observe(mainAtc);
    })();

    /* ── Initial render pass ──────────────────────────────────────────── */
    if (currentVariant) {
      setVariant(currentVariant, { skipUrl: !urlVariantId });
      // Land on first slide unless the variant has its own featured media.
      if (!currentVariant.featured_media) goTo(0);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
